/**
 * The knowledge base's strings (piece 30), kept beside its rules and spread
 * into the one lookup in `strings.ts`, so `t()` stays the only way a screen
 * reads text. 🔨 This file imports nothing, because the lookup imports it.
 */
export const KNOWLEDGE_STRINGS = {
  'kb.title': 'Knowledge Base',
  'whatsNew.area.knowledge': 'Knowledge Base',
  'kb.subtitle': 'Procedures, fault codes and training, written by the people who do the work',
  'kb.toc.title': 'Contents',
  'kb.toc.empty': 'This article has no headings yet.',
  'kb.version': 'Version',
  'kb.publishedOn': 'Published',
  'kb.articleBody': 'The article',
  'kb.reading.legend': 'Read the published version or the draft',
  'kb.reading.published': 'Published',
  'kb.reading.draft': 'Draft',
  'kb.draft.notice': 'You are reading the draft. Nobody else sees these words until the draft is published.',
  'kb.draft.noticeUnpublished': 'You are reading the draft. This article has not been published yet, so nobody else can read it.',
  'kb.draft.unchanged': 'The draft holds no change from version {n}.',
  'kb.draft.empty': 'The draft holds no words yet. Edit it to write some.',
  'kb.draft.savedOn': 'Draft last saved',
  'kb.linkedTag': 'KB linked',
  'rte.style': 'Style',
  'rte.style.normal': 'Normal',
  'rte.style.heading1': 'Heading 1',
  'rte.style.heading2': 'Heading 2',
  'rte.style.heading3': 'Heading 3',
  'rte.style.quote': 'Quote',
  'rte.style.code': 'Code block',
  // 🔒 The three callouts (T3572; decision 10530): the style menu's entries and the word each callout opens on.
  'kb.callout.note': 'Note',
  'kb.callout.warning': 'Warning',
  'kb.callout.tip': 'Tip',
  'rte.callout.refused': 'A callout holds words and lists only, so a table, a picture, a rule or the contents stays outside it.',
  'rte.toc': 'Insert table of contents',
  'rte.link': 'Link',
  'rte.link.address': 'Link address',
  'rte.link.placeholder': 'https://',
  'rte.link.apply': 'Add the Link',
  'rte.link.remove': 'Remove the Link',
  'rte.link.problem': 'A link starts with https://, http://, mailto: or tel:',
  'rte.rule': 'Horizontal line',
  'rte.table': 'Insert a table',
  'rte.table.rows': 'Rows',
  'rte.table.columns': 'Columns',
  'rte.table.header': 'The first row is a header row',
  'rte.table.insert': 'Insert the Table',
  'rte.table.problem': 'A table has 1 to 20 rows and 1 to 10 columns',
  // The control bar under the toolbar (T397; decision 1240).
  'rte.bar.table': 'Table',
  'rte.bar.tableLabel': 'Table controls',
  'rte.bar.row': '1 row',
  'rte.bar.rows': '{n} rows',
  'rte.bar.column': '1 column',
  'rte.bar.columns': '{n} columns',
  'rte.bar.phoneSize': '{rows} × {columns}',
  'rte.bar.rowAbove': 'Add Row Above',
  'rte.bar.rowBelow': 'Add Row Below',
  'rte.bar.rowRemove': 'Remove Row',
  'rte.bar.columnLeft': 'Add Column Left',
  'rte.bar.columnRight': 'Add Column Right',
  'rte.bar.columnRemove': 'Remove Column',
  'rte.bar.header': 'Header Row',
  'rte.bar.headerOn': '✓ Header Row',
  'rte.bar.delete': 'Delete Table',
  'rte.bar.keep': 'Keep It',
  'rte.bar.deleteAsk': 'Delete the whole table, {size}? Its words are deleted with it.',
  'rte.bar.maxRows': 'A table has at most 20 rows.',
  'rte.bar.maxColumns': 'A table has at most 10 columns.',
  'rte.bar.lastRow': 'A table keeps at least one row. Delete Table removes the whole table.',
  'rte.bar.lastColumn': 'A table keeps at least one column. Delete Table removes the whole table.',
  'rte.bar.aboveHeader': 'The header row stays the table’s first row, so a new row goes below it.',
  'rte.bar.rowMenu': 'Row',
  'rte.bar.columnMenu': 'Column',
  'rte.bar.tableMenu': 'Table',
  'rte.bar.announce.table': 'Table, {size}. Alt F10 opens its controls.',
  // The picture bar (T372; decisions 1242, 1243).
  'rte.bar.picture': 'Picture',
  'rte.bar.pictureLabel': 'Picture controls',
  'rte.bar.announce.picture': 'Picture, {size}, {align}. Alt F10 opens its controls.',
  'rte.picture.small': 'Small',
  'rte.picture.medium': 'Medium',
  'rte.picture.large': 'Large',
  'rte.picture.full': 'Full Width',
  'rte.picture.or': 'or',
  'rte.picture.percentLabel': 'Percent of the column’s width',
  'rte.picture.ofColumn': '% of the column',
  'rte.picture.another': 'Another Percent',
  'rte.picture.menuNamed': '{name} · {n}%',
  'rte.picture.menuOwn': 'Own Size',
  'rte.picture.ownSize': 'its own size',
  'rte.picture.percentOnly': '{n}%',
  'rte.picture.left': 'Left',
  'rte.picture.centre': 'Center',
  'rte.picture.right': 'Right',
  'rte.picture.heard.left': 'left',
  'rte.picture.heard.centre': 'centered',
  'rte.picture.heard.right': 'right',
  'rte.picture.remove': 'Remove Picture',
  'rte.picture.alt': 'Alt Text',
  'rte.picture.caption': 'Caption',
  // T1259 (decision 1942): the picture's desired minimum width on screen, in pixels.
  'rte.picture.minWidthLabel': 'Min Width',
  'rte.picture.pixels': 'px',
  'rte.picture.minWidthRefused': 'A minimum width is 100 to 1200 pixels.',
  'rte.picture.line.minimum': 'On screen it is never narrower than {n} pixels, or the column’s whole width where the column is narrower. The PDF keeps the percent.',
  'kb.picture.viewerLabel': 'Picture at full size',
  'kb.picture.close': 'Close the picture',
  'kb.picture.open': 'Open the picture at full size',
  'kb.picture.openNamed': 'Open the picture at full size: {alt}',
  'rte.picture.line.small': 'Small is a quarter of the column’s width.',
  'rte.picture.line.medium': 'Medium is half the column’s width.',
  'rte.picture.line.large': 'Large is three quarters of the column’s width.',
  'rte.picture.line.full': 'Full Width is the column’s whole width.',
  'rte.picture.line.percent': 'This picture is {n} percent of the column’s width.',
  'rte.picture.line.same': 'The article and its PDF draw it the same.',
  'rte.picture.line.own': 'This picture draws at its own size, up to the column’s width.',
  'rte.picture.refused': 'A picture is 10 to 100 percent of the column’s width.',
  'rte.picture.noAlt': 'A picture with no alt text is read aloud as “picture” and nothing more. Say what it shows.',
  'rte.picture.uploading': 'Uploading the picture…',
  'rte.picture.failed': 'The picture was not uploaded',
  // The list bar (T397; decisions 1270, 1271, 1272).
  'rte.bar.listLabel': 'List controls',
  'rte.bar.listBulleted': 'Bulleted List',
  'rte.bar.listNumbered': 'Numbered List',
  'rte.bar.announce.bulleted': 'Bulleted list, level {level}. Alt F10 opens its controls.',
  'rte.bar.announce.numbered': 'Numbered list, level {level}. Alt F10 opens its controls.',
  'rte.list.level': 'Level {n}',
  'rte.list.item': '1 item',
  'rte.list.items': '{n} items',
  'rte.list.underStep': 'nested under step {n}',
  'rte.list.underItem': 'nested under item {n}',
  'rte.list.bulleted': 'Bulleted',
  'rte.list.numbered': 'Numbered',
  'rte.list.startAt': 'Start At',
  'rte.list.indent': 'Indent',
  'rte.list.outdent': 'Outdent',
  'rte.list.remove': 'Remove List',
  'rte.list.firstItem': 'The first item has no item above it to nest under.',
  'rte.list.maxDepth': 'A list nests at most four levels deep.',
  'rte.numbering.startRefused': 'The Start At box takes a whole number from 1 to 999.',
  'rte.list.startRefused': 'A numbered list starts at a whole number from 1 to 999.',
  'rte.list.line.indentStep': 'Indent nests step {n} under step {m}.',
  'rte.list.line.indentItem': 'Indent nests item {n} under item {m}.',
  'rte.list.line.outdentOut': 'Outdent takes it out of the list as a paragraph.',
  'rte.list.line.outdentStep': 'Outdent undoes the nesting. This item becomes step {n} of the numbered list.',
  'rte.list.line.outdentItem': 'Outdent undoes the nesting. This item becomes item {n} of the bulleted list.',
  'rte.list.line.outdentBare': 'Outdent undoes the nesting.',
  'rte.panelCancel': 'Cancel',

  'kb.article.kindLine': 'Knowledge Base article',
  'kb.manage.subtitle': 'The sections articles are filed under, who reads each, and who writes in it',
  'kb.new': '+ New Article',
  'kb.new.title': 'New Article',
  'kb.new.create': 'Create the Draft',
  'kb.new.hint': 'The draft opens on its own page, where the words are written. Nothing is readable until it is published.',
  'kb.new.trainingHint': 'Tick the training box and the article gets a required quiz; Write the quiz then sits on its header.',
  'kb.import': 'Import',
  'kb.import.title': 'Import a File as an Article',
  'kb.import.file': 'File',
  'kb.import.noFile': 'No file chosen yet',
  'kb.import.kinds': 'A PDF, a Word document (.docx or .doc), a plain text file (.txt), a web page (.html) or a Markdown file (.md).',
  'kb.import.run': 'Import the file',
  'kb.import.fileNeeded': 'Choose a file to import first',
  'kb.import.tooLarge': 'This file is larger than a single upload may be, which is',
  'kb.import.reading': 'Reading the file and writing the draft…',
  'kb.import.hint': 'The file is read into a draft that only you and the article’s editors can see. Check it against your own copy of the file, correct it and publish it as any draft; the file itself is not kept.',
  'kb.import.refused.kind': 'The import reads a PDF, a Word document (.docx), a plain text file (.txt), a web page (.html) or a Markdown file (.md). This file is none of those.',
  'kb.import.refused.legacyDoc': 'This is a Word 97–2003 document (.doc), which this server cannot convert. Open it in Word, save it as a .docx and import that.',
  'kb.import.refused.scan': 'This PDF holds pictures of pages and no text, so it is a scan with nothing to read. Run it through text recognition (OCR) first, or import the original document.',
  'kb.import.refused.locked': 'This file is locked with a password. Open it, save a copy without the password and import that.',
  'kb.import.refused.empty': 'This file holds no words to import.',
  'kb.import.refused.unreadable': 'This file could not be read and may be damaged. Open it in the program that made it, save it again and import that.',
  'kb.import.leftOut': 'Picture not imported:',
  'kb.import.leftOut.type': 'the article keeps a PNG, JPEG, GIF or WebP picture, and this is none of those',
  'kb.import.leftOut.size': 'the picture is over 5 MB',
  'kb.import.leftOut.remote': 'the page only links to it on the internet, and an article never loads a picture from there',
  'kb.import.leftOut.relative': 'the file names it by a path to a file beside it, and the import receives only the file itself',
  'kb.import.pdfPicture': 'the picture on page',
  'kb.import.writing': 'Writing the draft…',
  'kb.import.ask.intro': 'The reader was unsure about part of this file. Answer each question and the draft is written the way you choose. Nothing is written until you finish.',
  'kb.import.ask.progress': 'Question {n} of {total}',
  'kb.import.ask.passage': 'In the file',
  'kb.import.ask.picturePlace': '[the picture]',
  'kb.import.ask.next': 'Next',
  'kb.import.ask.back': 'Back',
  'kb.import.ask.write': 'Write the draft',
  'kb.import.ask.runningLines': 'These lines repeat at the same height on most pages, the way a running header or footer does. Leave them out of the draft?',
  'kb.import.ask.runningLines.short': 'Running headers and footers',
  'kb.import.ask.runningLines.leave': 'Leave them out',
  'kb.import.ask.runningLines.keep': 'Keep them',
  'kb.import.ask.runningLines.each': 'Show me each',
  'kb.import.ask.runningLine': 'This line repeats on {n} pages. Leave it out of the draft?',
  'kb.import.ask.runningLine.leave': 'Leave it out',
  'kb.import.ask.runningLine.keep': 'Keep it',
  'kb.import.ask.letteredRun': 'A lettered list follows a numbered step. Where does it belong?',
  'kb.import.ask.letteredRun.short': 'A lettered list under a step',
  'kb.import.ask.letteredRun.nest': 'Nest it under step {n}',
  'kb.import.ask.letteredRun.nestAbove': 'Nest it under the step above',
  'kb.import.ask.letteredRun.own': 'Keep it as its own list',
  'kb.import.ask.styledLine': 'This short line is set wholly in italic or underlined. Is it a heading?',
  'kb.import.ask.styledLine.short': 'A short italic or underlined line',
  'kb.import.ask.styledLine.heading': 'A heading',
  'kb.import.ask.styledLine.paragraph': 'Part of the paragraph',
  'kb.import.ask.pictureBetween': 'This picture stands between two steps of a list. Where does it go?',
  'kb.import.ask.pictureBetween.short': 'A picture between two steps',
  'kb.import.ask.pictureBetween.here': 'Here, between steps {a} and {b}',
  'kb.import.ask.pictureBetween.hereOnPage': 'Here, where the page shows it',
  'kb.import.ask.pictureBetween.end': 'At the end of the page’s words',
  'kb.import.ask.resumedList': 'This numbered list opens at {n}, carrying on from the list before it. How is it numbered?',
  'kb.import.ask.resumedList.short': 'A list that carries on',
  'kb.import.ask.resumedList.continue': 'Continue at {n}',
  'kb.import.ask.resumedList.restart': 'Start again at 1',
  'kb.import.ask.tabPairs': 'Each of these lines is a label, a tab and a value. How do they come in?',
  'kb.import.ask.tabPairs.short': 'Lines lined up with tabs',
  'kb.import.ask.tabPairs.table': 'A two-column table',
  'kb.import.ask.tabPairs.indent': 'Indented text',
  'kb.import.ask.callout': 'This passage opens with Note, Warning, Caution or Tip. Does it come in as a callout?',
  'kb.import.ask.callout.short': 'A passage opening with Note, Warning or Tip',
  'kb.import.ask.callout.callout': 'A callout',
  'kb.import.ask.callout.paragraph': 'Keep it as a paragraph',
  'kb.import.report.title': 'Ready to write the draft',
  'kb.import.report.linesLeftOut': '{n} running header and footer lines will be left out.',
  'kb.import.report.asked': 'What you were asked, and your answer',
  'kb.import.report.row': '{question}: {answer}',
  'kb.import.report.nothingLeftOut': 'No running header or footer lines will be left out.',
  'kb.list.trainingHint': 'An article becomes training when its training box is ticked, in + New article or in Edit; it then carries a required quiz.',
  'kb.training.open': 'Training: open the article at its required quiz',
  'kb.field.title': 'Title',
  'kb.field.category': 'Category',
  'kb.field.visibility': 'Who can read it',
  'kb.field.groupRole': 'Readable by the role',
  'kb.field.groupDepartment': 'Readable by the department',
  'kb.field.training': 'This article is training, with a required quiz',
  'kb.field.certifies': 'A pass certifies',
  'kb.field.body': 'Article',
  'kb.visibility.public': 'Public',
  'kb.visibility.internal': 'Internal',
  'kb.visibility.group': 'Group',
  'kb.group.noRole': 'No role',
  'kb.group.noDepartment': 'No department',
  'kb.group.hint': 'A Group article names one role or one department, not both, and is refused on save with neither.',
  'kb.certifies.none': 'Nothing yet',
  'kb.certification.entity': 'Certification',
  'kb.certification.name': 'Name',
  'kb.certification.category': 'Applies to',
  'kb.certification.safety': 'Safety',
  'kb.certification.aptitude': 'Aptitude',
  'kb.certification.validity': 'Lapses after (months)',
  'kb.certification.hint': 'A pass of this training article writes the certification onto the person. Leave the months blank for one that never lapses.',
  'kb.certification.nameNeeded': 'Type the certification’s name first.',
  'kb.category.entity': 'Category',
  'kb.category.newHint': 'The category is added at the top of the tree. Nest it under another on the Categories page.',
  'kb.category.all': 'Every category',
  'kb.category.none': 'No category',
  'kb.category.top': 'At the top of the tree',
  'kb.category.empty': 'No category yet. Name one and add it at the top of the tree.',
  'kb.category.newName': 'New category name',
  'kb.category.addRoot': '+ Add at the Top',
  'kb.category.addBeneath': '+ Add Beneath the Selected One',
  'kb.category.nameNeeded': 'Type the category’s name first.',
  'kb.category.name': 'Name',
  'kb.category.parent': 'Sits beneath',
  'kb.category.order': 'Position',
  'kb.category.save': 'Save the Category',
  'kb.category.retire': 'Retire',
  'kb.category.restore': 'Put Back in Use',
  'kb.category.retired': 'Retired',
  'kb.category.pick': 'Pick a category in the tree to see its settings and its tiers.',
  'kb.category.readOnly': 'Your role does not hold Manage Categories here, so the category is read-only to you.',
  'kb.manage': 'Manage Categories',
  'kb.manage.title': 'Knowledge Base Categories',
  'kb.manage.tree': 'Category tree',
  // 🔒 Decision 2281: the Knowledge section's three tabs, and the Checklists tab's own title and kind line.
  'kb.tabs.legend': 'Knowledge Base',
  'kb.tabs.articles': 'Articles',
  'kb.tabs.categories': 'Categories',
  'kb.tabs.checklists': 'Checklists',
  'kb.checklists.title': 'Checklist Templates',
  'kb.checklists.usedIn': 'Used in Checklists',
  'kb.checklists.usedInNone': 'No active checklist cites this article.',
  'kb.publish.citing': 'These checklists send technicians to this procedure:',
  'kb.checklists.subtitle': 'The steps a job follows, each item typed and able to cite the procedure it follows',
  'kb.tiers': 'Author tiers',
  'kb.tiers.default': 'No category above this one grants a tier, so everyone in the company writes and edits their own articles here.',
  'kb.tiers.inherits': 'Holds no tier of its own, so it takes the tiers of',
  'kb.tiers.role': 'Role',
  'kb.tiers.tier': 'Tier',
  'kb.tiers.pickRole': 'Pick a role',
  'kb.tiers.pickTier': 'Pick a tier',
  'kb.tiers.pickBoth': 'Pick the role and the tier first.',
  'kb.tiers.grant': 'Grant the Tier',
  'kb.tiers.revoke': 'Revoke',
  'kb.tiers.hint': 'The tiers are a ladder: each holds the ones before it. Update and Delete reach the articles a person wrote; Update/Delete All reaches every article here. Only Admin Access grants a tier.',
  'kb.tier.view': 'View',
  'kb.tier.create': 'Create',
  'kb.tier.update': 'Update',
  'kb.tier.delete': 'Delete',
  'kb.tier.updateDeleteAll': 'Update/Delete All',
  'kb.tier.manageCategories': 'Manage Categories',
  'kb.tier.adminAccess': 'Admin Access',
  'kb.flag.title': 'Question failure flag',
  'kb.flag.hint': 'When a question’s first-attempt failure rate reaches this percentage over at least this many first attempts, a to-do reaches the article’s author. A high failure rate is read as a problem with the material, not with the people.',
  'kb.flag.threshold': 'Failure rate that flags a question (%)',
  'kb.flag.minimum': 'First attempts before a question can be flagged',
  // Decision 1202: the two numbers are edited in Settings › Knowledge base, and the category screen links there.
  'kb.flag.change': 'Change in Settings',
  // The Settings entry's own title, the one its Index card reads (decision 1202).
  'kb.flag.settingsTitle': 'Quiz Question Flag',
  'kb.search.label': 'Search the articles',
  'kb.search.placeholder': 'Words in the title, the text or the category',
  'kb.search.run': 'Search',
  'kb.search.needsWord': 'Type a word to search for',
  'kb.search.bodyMatches': 'Body matches',
  'kb.search.results': 'result(s)',
  'kb.search.none': 'No article you can open matches those words.',
  'kb.list.empty': 'No article yet. + New article starts one.',
  'kb.archived.show': 'Show Archived',
  'kb.archived.hide': 'Hide Archived',
  'kb.neverPublished': 'Never published',
  'kb.training': 'Training',
  'kb.article.missing': 'This article is not one you can open, or it does not exist.',
  'kb.exportPdf': 'Export to PDF',
  'kb.exportPdf.unpublished': 'An article exports once it has been published.',
  'kb.edit': 'Edit',
  'kb.publish': 'Publish',
  'kb.archive': 'Archive',
  'kb.save.tooLarge': 'This article is too large to save. Remove some pictures or split it into two articles, then save again.',
  'kb.archive.title': 'Archive this article?',
  'kb.archive.consequence': 'The article leaves the list and stays readable, with every version, because somebody may have been certified against it.',
  'kb.delete': 'Delete Draft',
  'kb.deleteArchived': 'Delete Article',
  'kb.deleteArchived.subject': 'the archived article {title}, with every version, its quiz, its tags, its comments and its pictures',
  'kb.olderVersion': 'This is an earlier version of the article.',
  'kb.readNewest': 'Read the newest version',
  'kb.unpublished': 'This article has not been published yet, so nobody else can read it. Edit it, then Publish.',
  'kb.files': 'Supporting files',
  // Decision 9580 (T3369): an article's comments, on the web's article page and the phone's.
  'kb.comments': 'Comments',
  'kb.comments.none': 'No comment yet.',
  'kb.comment.label': 'New comment',
  'kb.comment.placeholder': 'Write a comment…',
  'kb.comment.add': 'Add Comment',
  'kb.number.prefix': 'KB-',
  'kb.comment.empty': 'Type a comment before adding it.',
  'kb.comment.notAdded': 'The comment was not added.',
  'kb.comment.needsSignal': 'A comment is added with a signal; nothing was sent. Press Add Comment again once the phone has one.',
  'kb.comments.loading': 'Reading the comments…',
  'kb.versions': 'Versions',
  'kb.versions.none': 'No version yet. Publishing makes version 1.',
  'kb.recertified': 'Required recertification',
  'kb.notRecertified': 'Earlier certifications stood',
  'kb.publish.title': 'Publish a New Version',
  'kb.publish.saveTitle': 'Save and Publish',
  'kb.publish.savePrimary': 'Save and Publish',
  'kb.publish.saveFirst': 'The editor holds changes that are not saved yet. They are saved as the draft first, and then the draft is published.',
  'kb.publish.saveMissing': 'The editor could not save the draft, so nothing was published. Press Save the draft, then Publish.',
  'kb.publish.question': 'Does this change what somebody has to do differently?',
  'kb.publish.yes': 'Yes — everybody certified on an earlier version has to pass again',
  'kb.publish.no': 'No — a typo, a link or clearer wording; earlier certifications stand',
  'kb.publish.hint': 'Neither answer is chosen for you. Publishing makes the next version, which is what readers see and what a quiz pass names.',
  'kb.publish.summary': 'What changed',
  'kb.tags': 'Tagged records',
  'kb.tags.none': 'Tagged to nothing. Most articles document nothing in particular; one about a part or an asset is tagged to it.',
  'kb.tag.item': 'Item',
  'kb.tag.asset': 'Asset',
  'kb.tagRecord': '+ Tag Record',
  'kb.tagRecord.entity': 'asset or item',
  'kb.linkFile': 'Link a file',
  'kb.linkFile.pick': 'Pick a file on a tagged record',
  'kb.linkFile.none': 'Tag the article to a part or an asset to link one of its files.',
  'kb.saveDraft': 'Save the Draft',
  'kb.saveDraft.hint': 'Saving keeps the draft and makes no version. Readers keep seeing the last published version until you Publish.',
  'kb.titleNeeded': 'An article needs a title.',
  'kb.quiz.tile': 'Required Quiz',
  'kb.quiz.unwritten': 'No quiz has been written for this training yet.',
  'kb.quiz.questions': 'question(s)',
  'kb.quiz.passAt': 'pass at',
  'kb.quiz.notPublished': 'The quiz as written publishes with the article’s next version.',
  'kb.quiz.notYetPassed': 'You have not passed this quiz yet.',
  'kb.quiz.expires': 'expires',
  'kb.quiz.lastFailed': 'Your last attempt did not pass:',
  'kb.quiz.waitBefore': 'You can sit it again in',
  'kb.quiz.minutes': 'minute(s).',
  'kb.quiz.certifiesNothing': 'A pass certifies nothing until the article names the requirement it certifies.',
  'kb.quiz.take': 'Take the Quiz',
  'kb.quiz.retake': 'Sit It Again',
  'kb.quiz.cooldown': 'The retake wait has not passed yet.',
  'kb.quiz.edit': 'Edit the Quiz',
  'kb.quiz.write': 'Write the Quiz',
  'kb.quiz.report': 'Question Report',

  'kb.flag.todo': 'A quiz question is failed too often',
  'kb.flag.todoDetail': 'of first attempts failed it, over',
  'kb.personReport.link': 'Right and wrong answers by quiz',
  'kb.personReport.linkDetail': 'Where this person’s training has gaps, by quiz and by article category',
  'jobs.expireRequirementCompletions.label': 'Expire lapsed certifications',
  'jobs.expireRequirementCompletions.purpose': 'Moves every certification whose expiry date has passed from Current to Expired, whatever earned it, then raises the Certification Register’s renewal and missing-training to-dos.',

  'kb.quiz.kindLine': 'Required quiz',
  'kb.quiz.editKindLine': 'Required quiz · authoring',
  'kb.report.kindLine': 'Question report',
  'kb.personReport.kindLine': 'Training answers',
  'kb.questionType.singleChoice': 'Single choice',
  'kb.questionType.multipleChoice': 'Multiple choice, more than one right',
  'kb.questionType.trueFalse': 'True or false',
  'kb.quiz.backToArticle': 'Back to the Article',
  'kb.quiz.previewBanner': 'Preview: you are sitting the quiz as written now, as a taker would. Nothing is recorded.',
  'kb.quiz.none': 'This article has no published quiz to sit yet.',
  'kb.quiz.passed': 'Passed',
  'kb.quiz.failed': 'Not passed',
  'kb.quiz.score': 'Score',
  'kb.quiz.certified': 'Your certification is recorded on your training record, naming the version of the article you passed.',
  'kb.quiz.wrongNamed': 'The questions you answered wrongly are marked below. The right answers are not shown: read the article again, then sit it again.',
  'kb.quiz.answeredWrongly': 'Answered wrongly',
  'kb.quiz.pickAll': 'Pick every answer that is right.',
  'kb.quiz.pickOne': 'Pick one',
  'kb.quiz.submit': 'Submit My Answers',
  'kb.quiz.gradePreview': 'Grade My Preview',
  'kb.quiz.answerAll': 'Answer every question first.',
  'kb.quiz.editTitle': 'Write the Required Quiz',
  'kb.quiz.editRefused': 'Only somebody who may edit this article writes its quiz.',
  'kb.quiz.editHint': 'The quiz is published with the article’s next version, and a person always sits the version published last. Changing a question changes nothing anybody has already passed.',
  'kb.quiz.savedHint': 'Saved. Takers see it once the article is published again.',
  'kb.quiz.passingScore': 'Passing score (%)',
  'kb.quiz.cooldownMinutes': 'Wait before a retake (minutes)',
  'kb.quiz.cooldownHint': 'Leave the wait empty for none. Retakes are always allowed; a wait is for a certification that needs teeth.',
  'kb.quiz.noQuestions': 'No question yet.',
  'kb.quiz.question': 'Question',
  'kb.quiz.type': 'Type',
  'kb.quiz.correctAnswer': 'The right answer',
  'kb.quiz.true': 'True',
  'kb.quiz.false': 'False',
  'kb.quiz.answer': 'Answer',
  'kb.quiz.correct': 'Right answer',
  'kb.quiz.removeAnswer': 'Remove',
  'kb.quiz.twoAnswers': 'A question keeps at least two answers.',
  'kb.quiz.addAnswer': '+ Add Answer',
  'kb.quiz.removeQuestion': 'Remove Question',
  'kb.quiz.addQuestion': '+ Add Question',
  'kb.quiz.save': 'Save the Quiz',
  'kb.quiz.passingNeeded': 'A passing score is needed.',
  'kb.quiz.preview': 'Preview as a Taker',
  'kb.quiz.saveBeforePreview': 'Save the quiz before previewing it.',
  'kb.report.title': 'Question Report',
  'kb.report.refused': 'The question report is read by the authors and trainers of the article’s category.',
  'kb.report.hint': 'First attempts only, one quiz version at a time',
  'kb.report.flagAt': 'a question is flagged at',
  'kb.report.over': 'over first attempts numbering at least',
  'kb.report.none': 'Nobody has sat a published version of this quiz yet.',
  'kb.report.quizVersion': 'Quiz version',
  'kb.report.sittings': 'sitting(s)',
  'kb.report.firstAttempts': 'first attempt(s)',
  'kb.report.failedFirst': 'failed',
  'kb.report.noRate': 'no rate yet',
  'kb.report.unanswered': 'Left unanswered',
  'kb.report.flagged': 'Flagged',
  'kb.report.finding': 'Finding the question’s article…',
  'kb.personReport.title': 'Training Answers',
  'kb.personReport.refused': 'A person’s answers are read by that person and by HR.',
  'kb.personReport.byQuiz': 'By quiz',
  'kb.personReport.byCategory': 'By category',
  'kb.personReport.completions': 'Certifications',
  'kb.personReport.noAttempts': 'No quiz sat yet.',
  'kb.personReport.noCompletions': 'No certification recorded yet.',
  'kb.personReport.attempts': 'sitting(s)',
  'kb.personReport.first': 'first',
  'kb.personReport.best': 'best',
  'kb.personReport.right': 'right',
  'kb.personReport.wrong': 'wrong',
} as const;
